/**
 * Elapsed-time (DURATION) helpers for the exposure runner.
 *
 * WHY THIS EXISTS. A recorded reaction's "time" is not a time of day — it is how long the person
 * had been in the exposure when they logged that reading. The runner used to render it by adding
 * the offset to a synthetic MIDNIGHT and handing that `Date` to a wall-clock picker, so correcting
 * a reading meant answering a 12-hour AM/PM dial whose value means nothing to anyone (founder,
 * 2026-09-07: *"it is opening a strange view that shows actual clock time with AM instead of a UI
 * to easily modify duration"*). A duration is minutes and seconds, so these functions speak in
 * seconds and in minutes/seconds fields, and the midnight arithmetic is gone.
 *
 * Everything here is pure and total: it never throws, and a nonsense input degrades to zero rather
 * than to `NaN` reaching the UI. No human-readable strings live here by design — the labels around
 * these numbers come from i18n at the call site.
 *
 * IT LIVES IN `@cc/utils` BECAUSE BOTH SURFACES SHOW THE SAME NUMBER (founder, 2026-09-08: he opened
 * a saved session in the portal and met wall-clock times where the app shows elapsed — *"I thought it
 * was time since the start of the exposure setting"*). The app and the old app before it have always
 * shown elapsed; the portal's recorded-point chips were the odd one out. One implementation, so the
 * two cannot drift again.
 */

/** Whole, non-negative seconds. `NaN`/`Infinity`/negative all collapse to 0. */
export function normalizeDurationSeconds(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, Math.floor(value));
}

/**
 * `H:mm:ss` — the runner's long-standing elapsed format. `padHours` gives the `HH:mm:ss` variant
 * the run's big timer has always used, so neither display changes shape.
 */
export function formatDurationHms(totalSeconds: number, padHours = false): string {
  const safeSeconds = normalizeDurationSeconds(totalSeconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  return [
    padHours ? String(hours).padStart(2, '0') : String(hours),
    String(minutes).padStart(2, '0'),
    String(seconds).padStart(2, '0'),
  ].join(':');
}

/** The two boxes the duration editor shows. `minutes` is allowed to exceed 59 (it carries hours). */
export type DurationFields = {
  minutes: string;
  seconds: string;
};

export function toDurationFields(totalSeconds: number): DurationFields {
  const safeSeconds = normalizeDurationSeconds(totalSeconds);

  return {
    minutes: String(Math.floor(safeSeconds / 60)),
    seconds: String(safeSeconds % 60),
  };
}

/**
 * Digits only — applied on every keystroke so a field can never hold something the parser has to
 * reject, which is what lets the editor's Save button need no error copy at all.
 */
export function sanitizeDurationInput(raw: string): string {
  return raw.replace(/[^0-9]/g, '');
}

function parseDurationPart(raw: string): number | null {
  const trimmedValue = raw.trim();

  if (!trimmedValue) {
    // an empty box reads as zero — "3 minutes flat" should not need a typed 0
    return 0;
  }

  if (!/^\d+$/.test(trimmedValue)) {
    return null;
  }

  const parsedValue = Number.parseInt(trimmedValue, 10);

  return Number.isSafeInteger(parsedValue) ? parsedValue : null;
}

/**
 * Fields → seconds. Seconds ≥ 60 roll over into minutes rather than being refused, so a person
 * typing "90" in the seconds box gets the minute and a half they meant. `null` only when a field
 * holds something that is not a whole number, which the sanitizer already prevents.
 */
export function fromDurationFields(fields: DurationFields): number | null {
  const minutes = parseDurationPart(fields.minutes);
  const seconds = parseDurationPart(fields.seconds);

  if (minutes === null || seconds === null) {
    return null;
  }

  return minutes * 60 + seconds;
}
